import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ShopDiscovery from '../components/ShopDiscovery';
import { Shop } from '../services/shopService';
import CustomerHeader from '../components/CustomerHeader';
import { useTranslations } from '../hooks/useTranslations';
import { useCartCount } from '../hooks/useCartCount';
import { useLanguage } from '../hooks/useLanguage';
import '../App.css';
import './ExploreShopsPage.css';
import './CustomerHomePage.css';

const ExploreShopsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated: _isAuthenticated, user: _user } = useAuth();
  const t = useTranslations();
  const [initialShopType, setInitialShopType] = useState<string | null>(null);
  const [categoryInfo, setCategoryInfo] = useState<{
    name?: string;
    displayName?: string;
    image?: string;
    icon?: string;
  } | null>(null);
  const [_searchQuery, _setSearchQuery] = useState('');
  
  // Use shared hooks for cart count and language
  const cartCount = useCartCount(1000); // Poll every 1000ms (matching original interval)
  const { language, setLanguage: _setLanguage } = useLanguage();

  // Get shop type filter and category info from navigation state
  useEffect(() => {
    const state = location.state as any;
    if (state?.shopType) {
      setInitialShopType(state.shopType);
      console.log('🔍 ExploreShopsPage: Setting initial shop type filter:', state.shopType);
    }
    if (state?.categoryName || state?.categoryDisplayName || state?.categoryImage || state?.categoryIcon) {
      setCategoryInfo({
        name: state.categoryName,
        displayName: state.categoryDisplayName || state.categoryName,
        image: state.categoryImage,
        icon: state.categoryIcon
      });
      console.log('🔍 ExploreShopsPage: Category info:', {
        name: state.categoryName,
        displayName: state.categoryDisplayName,
        image: state.categoryImage,
        icon: state.categoryIcon
      });
    }
  }, [location.state]);

  const handleSelectShop = (shop: Shop) => {
    // Navigate to dedicated shop page - ensure shop ID is valid
    if (!shop || !shop.id) {
      console.error('❌ ExploreShopsPage: Invalid shop data, cannot navigate');
      return;
    }
    
    const shopId = String(shop.id).trim();
    if (!shopId) {
      console.error('❌ ExploreShopsPage: Empty shop ID, cannot navigate');
      return;
    }
    
    console.log('🔍 ExploreShopsPage: Navigating to shop page:', {
      shopId: shopId,
      shopName: shop.name,
      route: `/shop/${shopId}`
    });
    
    // Navigate to shop page - this should show ONLY that shop's products
    navigate(`/shop/${shopId}`, { 
      replace: false, // Allow back navigation
      state: {
        fromExploreShops: true,
        shopName: shop.name
      }
    });
  };

  const handleViewAllShops = () => {
    // Navigate to app with all shops view
    navigate('/app', { state: { view: 'discovery' } });
  };

  const handleViewShopDetails = (shop: Shop) => {
    // Navigate to shop details page (details only, no products)
    console.log('🔍 ExploreShopsPage: Viewing shop details:', shop.id, shop.name);
    navigate(`/shop/${shop.id}/details`);
  };

  return (
    <div className="explore-shops-page">
      {/* Header - Same as Browse All page */}
      <CustomerHeader
        showBackButton
        showLoyaltyButton
        cartCount={cartCount}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Navigation Buttons */}
      <nav id="navigation" className="main-nav" role="navigation" aria-label="Primary">
        <div className="nav-content">
          <div className="nav-buttons">
            <button 
              className={`nav-btn ${location.pathname === '/app' || location.pathname === '/' || location.pathname === '/customer' || location.pathname === '/customer/home' ? 'active' : ''}`} 
              onClick={() => navigate('/app')}
            >
              🏠 {t.mainStore || 'Dashboard'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/explore-shops' || location.pathname === '/shops' ? 'active' : ''}`} 
              onClick={() => navigate('/explore-shops')}
            >
              🔎 {t.exploreShops || 'Explore Shops'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/browse-all' || location.pathname === '/browse' ? 'active' : ''}`} 
              onClick={() => navigate('/browse-all')}
            >
              🛍️ {t.browseAll || 'Browse All'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/services' || location.pathname.startsWith('/services/') ? 'active' : ''}`} 
              onClick={() => navigate('/services')}
            >
              🔧 {language === 'hi' ? 'सेवाएं' : language === 'te' ? 'సేవలు' : language === 'ta' ? 'சேவைகள்' : 'Services'}
            </button>
          </div>
        </div>
      </nav>

      {/* Shop Discovery Component */}
      <div className="explore-shops-content">
        <div className="body-container">
          <ShopDiscovery
            onSelectShop={handleSelectShop}
            onViewAllShops={handleViewAllShops}
            onViewShopDetails={handleViewShopDetails}
            initialShopType={initialShopType || undefined}
            categoryInfo={categoryInfo || undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default ExploreShopsPage;

